/**
 * illustrationDoc.js — client-side jsPDF composer for the Osteo3d Packaging
 * Illustration (label pasted on the box). Matches the reference layout:
 * /Users/deepakrajk/Documents/Temp/PackagingIllustration/ (P1.png / P2.png).
 *
 * A4 portrait, ONE product per page: big centered logo (the logo PNG already
 * carries the "3d printing healthcare®" tagline) + "Illustration" subtitle;
 * double-ruled 4-row info bar (Surgeon/Hospital/Patient/Case ID); Product name:
 * (wraps) + Product ID:; one large centered render (tiled "Osteo3d ®" watermark,
 * no corner label); branded footer. Printed at ¼ of an A4 and pasted on the box.
 *
 * Shares the tiled watermark, image helpers and brand colours with the other
 * Osteo3d documents via pdfChassis.js.
 */

import { PW, BRAND, asDataUrl, cropToContent, tiledWatermark } from './pdfChassis.js'

const M = 14
const USABLE = PW - 2 * M

const { DARK } = BRAND
// ⚠️ Intentionally NOT BRAND.GREY (128) / BRAND.INK (40). This label has always
// printed at 120 / 30, and it is a physical artifact pasted on the box — so the
// difference is left alone rather than silently unified. Almost certainly
// historical drift; raise it with the team before changing it.
const GREY = [120, 120, 120]
const INK = [30, 30, 30]

// NB: a narrower escape than the report's — only the arrow glyph, no smart-quote
// or back-arrow mapping. Kept as-is so the printed label does not move.
const esc = (v) => (v == null || v === '' ? '—' : String(v).replace(/→/g, '->'))

function footer(doc, appVersion) {
  doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.5)
  doc.line(M, 257, PW - M, 257)
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(0, 0, 0)
  doc.text('Images are not to scale', PW - M, 264, { align: 'right' })
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...GREY)
  doc.text(
    "Copyright © 2026 Osteo3d® . All rights reserved. 'Osteo3d®' and '3d printing healthcare®' are registered trademarks. Unauthorized use/replication is prohibited. We do not have any representatives or distributors",
    PW / 2, 270, { align: 'center', maxWidth: USABLE },
  )
  doc.setFontSize(9)
  doc.text('Phone: +91-9900265669        Email: contactus@osteo3d.com', PW / 2, 286, { align: 'center' })
  doc.setFontSize(8)
  doc.text(`Osteo3d®   Illustration ${appVersion || 'v1.0'}`, M, 292)
}

// Exported for tests: the info-bar row-collision regression is measured directly
// (jsPDF assigns .text per-instance, so tests patch a doc and pass it in here).
export async function drawPage(doc, header, product, logo, logoAspect) {
  let y = 12
  // header — logo (carries tagline) + "Illustration"
  if (logo) {
    const h = 30, w = h * (logoAspect || 3)
    try { doc.addImage(logo, 'PNG', (PW - w) / 2, y, w, h) } catch { /* ignore */ }
  }
  y = 48
  doc.setFont('helvetica', 'normal'); doc.setFontSize(15); doc.setTextColor(...DARK)
  doc.text('Illustration', PW / 2, y, { align: 'center' })

  // info bar (double-ruled), 4 rows Label : Value
  y = 53
  doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.5)
  doc.line(M, y, PW - M, y)
  const rows = [
    ['Surgeon name', header.surgeonName], ['Hospital name', header.hospitalName],
    ['Patient name', header.patientName], ['Case ID', header.caseId],
  ]
  // Values WRAP inside the value column and the row advances to fit (team req
  // 2026-07-28: multi-surgeon names need 2 lines). The old fixed `ry += 8` let a
  // wrapped value's 2nd line (drawn via maxWidth) collide with the next row's label.
  doc.setFontSize(13); doc.setTextColor(...INK)
  let ry = y + 7
  const colonX = M + 42
  const valueX = colonX + 4
  const valueW = PW - M - valueX
  const lineH = 5.8 // leading for continuation lines (13pt)
  for (const [label, val] of rows) {
    doc.setFont('helvetica', 'normal')
    doc.text(label, M, ry)
    doc.text(':', colonX, ry)
    const lines = doc.splitTextToSize(esc(val), valueW)
    lines.forEach((ln, i) => doc.text(ln, valueX, ry + i * lineH))
    ry += 8 + (lines.length - 1) * lineH
  }
  doc.line(M, ry - 2, PW - M, ry - 2)

  // Product name / ID
  y = ry + 10
  doc.setFontSize(15); doc.setTextColor(...INK)
  const nameLines = doc.splitTextToSize(`Product name: ${esc(product.productName)}`, USABLE)
  doc.text(nameLines, M, y)
  y += nameLines.length * 7 + 1
  doc.text(`Product ID: ${esc(product.productId)}`, M, y)

  // one large render — content-cropped to the model so it FILLS the box (footer at 257)
  if (product.image) {
    const boxY = Math.max(y + 6, 114), boxH = 254 - boxY
    const { url, ar } = await cropToContent(asDataUrl(product.image))
    let w = USABLE, h = USABLE / ar
    if (h > boxH) { h = boxH; w = boxH * ar }
    try { doc.addImage(url, 'PNG', (PW - w) / 2, boxY + (boxH - h) / 2, w, h) } catch { /* ignore */ }
  }
}

/**
 * @param {object} opts { header:{surgeonName,hospitalName,patientName,caseId},
 *   products:[{productName,productId,image}], logo, logoAspect, appVersion }
 * @returns {Promise<jsPDF>}
 */
export async function buildIllustration({ header = {}, products = [], logo = null, logoAspect = 3, appVersion = 'v1.0' }) {
  const { jsPDF } = await import('jspdf')
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
  const list = products.length ? products : [{ productName: '', productId: '', image: '' }]
  for (let i = 0; i < list.length; i++) {
    if (i > 0) doc.addPage()
    await drawPage(doc, header, list[i], logo, logoAspect)
  }
  const pages = doc.getNumberOfPages()
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p)
    tiledWatermark(doc, 'helvetica')
    footer(doc, appVersion)
  }
  return doc
}
